import React, { useState, useEffect, useMemo } from 'react';
import type {
  CalculationResult,
  PotentialDiscount,
  RedeemableProduct,
  RedeemableAddPayload,
  RedeemableSelectionItemPayload,
} from '../types';

interface RedeemableProductsProps {
  calculationResult: CalculationResult | null;
  onRedeemableAdd: (payload: RedeemableAddPayload) => Promise<boolean>;
}

export const RedeemableProducts: React.FC<RedeemableProductsProps> = ({
  calculationResult,
  onRedeemableAdd,
}) => {
  // State to track selected items for each discount
  const [selections, setSelections] = useState<Map<string, Map<number, string>>>(new Map());
  // State to track current slide index for each group
  const [slideIndices, setSlideIndices] = useState<Map<string, Map<number, number>>>(new Map());
  // State to track selected variant options for each product (key: discountId-groupIdx, value: Map<optionName, optionValue>)
  const [variantOptions, setVariantOptions] = useState<Map<string, Map<string, string>>>(new Map());
  const [addingDiscounts, setAddingDiscounts] = useState<Set<string>>(new Set());
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);

  const getDiscountStateKey = (discountId: string, discountIndex: number) => `${discountId}::${discountIndex}`;
  const getVariantOptionKey = (discountKey: string, groupIndex: number) => `${discountKey}-${groupIndex}`;
  const extractNumericId = (id: string | number | undefined | null): number | null => {
    if (id === undefined || id === null) {
      return null;
    }
    if (typeof id === 'number') {
      return id;
    }
    const normalized = id.startsWith('gid://')
      ? id.substring(id.lastIndexOf('/') + 1)
      : id;
    const parsed = parseInt(normalized, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };
  const parsePriceToCents = (price: string | number | undefined | null): number => {
    if (price === undefined || price === null) return 0;
    const numeric = typeof price === 'number' ? price : parseFloat(price);
    if (Number.isNaN(numeric)) return 0;
    return Math.round(numeric * 100);
  };

  // Collect all potential discounts with redeemable groups
  const potentialDiscounts: PotentialDiscount[] = [];

  if (calculationResult?.potential_discounts) {
    calculationResult.potential_discounts.forEach((potentialDiscount) => {
      if (potentialDiscount.redeemableGroups && potentialDiscount.redeemableGroups.length > 0) {
        potentialDiscounts.push(potentialDiscount);
      }
    });
  }

  const groupedDiscounts = useMemo(() => {
    const orderedGroups: Array<{
      discountId: string;
      representative: PotentialDiscount;
      representativeIndex: number;
      count: number;
    }> = [];
    const indexById = new Map<string, number>();

    potentialDiscounts.forEach((potentialDiscount, index) => {
      const existingGroupIndex = indexById.get(potentialDiscount.discount_id);
      if (existingGroupIndex === undefined) {
        indexById.set(potentialDiscount.discount_id, orderedGroups.length);
        orderedGroups.push({
          discountId: potentialDiscount.discount_id,
          representative: potentialDiscount,
          representativeIndex: index,
          count: 1,
        });
      } else {
        const existingGroup = orderedGroups[existingGroupIndex];
        existingGroup.count += 1;
      }
    });

    return orderedGroups;
  }, [potentialDiscounts]);

  // Auto-select single variant products when potentialDiscounts or slide changes
  useEffect(() => {
    potentialDiscounts.forEach((potentialDiscount, discountIndex) => {
      const discountKey = getDiscountStateKey(potentialDiscount.discount_id, discountIndex);
      potentialDiscount.redeemableGroups?.forEach((group, groupIdx) => {
        // Check current slide to get the displayed item
        const currentSlideIndex = slideIndices.get(discountKey)?.get(groupIdx) || 0;
        const currentItem = group.items[currentSlideIndex];

        if (currentItem) {
          const variants = currentItem.product_details?.product_data?.variants || [];
          const inStockVariants = variants.filter(
            (v: any) => v.inventoryQuantity === undefined || v.inventoryQuantity > 0
          );

          // Auto-select if product has only one in-stock variant (others may be OOS)
          if (inStockVariants.length === 1) {
            // Check if already selected using a fresh read
            setSelections((prev) => {
              const discountSelections = prev.get(discountKey);
              const isAlreadySelected = discountSelections?.has(groupIdx);

              if (!isAlreadySelected) {
                const singleVariant = inStockVariants[0];
                const variantId = singleVariant.id.startsWith('gid://shopify/ProductVariant/')
                  ? singleVariant.id.replace('gid://shopify/ProductVariant/', '')
                  : singleVariant.id;

                // Create new selections with the auto-selected variant
                const newSelections = new Map(prev);
                if (!newSelections.has(discountKey)) {
                  newSelections.set(discountKey, new Map());
                }
                const discountSelectionsMap = new Map(newSelections.get(discountKey) || new Map());
                discountSelectionsMap.set(groupIdx, variantId);
                newSelections.set(discountKey, discountSelectionsMap);
                return newSelections;
              }

              return prev; // No change needed
            });
          }
        }
      });
    });
  }, [potentialDiscounts.length, slideIndices]);

  const handleSelection = (discountId: string, discountIndex: number, groupIndex: number, variantId: string) => {
    const discountKey = getDiscountStateKey(discountId, discountIndex);
    setSelections((prev) => {
      // Create a completely new Map structure to avoid any reference issues
      const newSelections = new Map<string, Map<number, string>>();

      // Copy all existing selections
      prev.forEach((groups, did) => {
        newSelections.set(did, new Map(groups));
      });

      // Ensure discount exists
      if (!newSelections.has(discountKey)) {
        newSelections.set(discountKey, new Map());
      }

      // Update the specific selection (clear if variantId is empty)
      const discountSelections = newSelections.get(discountKey)!;
      if (variantId === '') {
        discountSelections.delete(groupIndex);
      } else {
        discountSelections.set(groupIndex, variantId);
      }

      return newSelections;
    });
  };

  const handleSlide = (
    discountId: string,
    discountIndex: number,
    groupIndex: number,
    direction: 'prev' | 'next',
    maxItems: number
  ) => {
    console.log(`Slide ${direction} for discount ${discountId} (index ${discountIndex}), group ${groupIndex}`);
    const discountKey = getDiscountStateKey(discountId, discountIndex);
    setSlideIndices((prev) => {
      const newIndices = new Map(prev);
      if (!newIndices.has(discountKey)) {
        newIndices.set(discountKey, new Map());
      }
      const discountIndices = new Map(newIndices.get(discountKey) || new Map());
      const currentIndex = discountIndices.get(groupIndex) || 0;
      const maxIndex = maxItems - 1;

      let newIndex = currentIndex;
      if (direction === 'prev') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : maxIndex;
      } else {
        newIndex = currentIndex < maxIndex ? currentIndex + 1 : 0;
      }

      discountIndices.set(groupIndex, newIndex);
      newIndices.set(discountKey, discountIndices);
      return newIndices;
    });
  };

  const getCurrentSlideIndex = (discountId: string, discountIndex: number, groupIndex: number): number => {
    const discountKey = getDiscountStateKey(discountId, discountIndex);
    return slideIndices.get(discountKey)?.get(groupIndex) || 0;
  };

  const formatPrice = (priceString: string): string => {
    const price = parseFloat(priceString);
    if (isNaN(price)) return 'N/A';
    return `RM ${price.toFixed(2)}`;
  };

  const getDiscountText = (product: RedeemableProduct, potentialDiscount?: PotentialDiscount, originalPrice?: number): string => {
    // Check discount_applicable_to first
    if (potentialDiscount?.discount_applicable_to === 'whole' && potentialDiscount.whole_discount_type && potentialDiscount.whole_discount_value !== undefined) {
      // Convert whole_discount_value to number to handle string values from API
      const wholeDiscountValue = typeof potentialDiscount.whole_discount_value === 'number' 
        ? potentialDiscount.whole_discount_value 
        : parseFloat(String(potentialDiscount.whole_discount_value)) || 0;
      
      if (potentialDiscount.whole_discount_type === 'fixed') {
        return `Save RM ${wholeDiscountValue.toFixed(2)}`;
      } else if (potentialDiscount.whole_discount_type === 'percentage') {
        return `${Math.round(wholeDiscountValue)}% off`;
      } else if (potentialDiscount.whole_discount_type === 'final_price') {
        // For final_price, calculate the discount based on buy items and show percentage off
        const buyItemsTotal = potentialDiscount.buyItems?.reduce((sum, buyItem) => {
          return sum + (buyItem.item.price || 0);
        }, 0) || 0;
        const combinedFinalPrice = wholeDiscountValue;
        const getItemFinalPrice = Math.max(0, combinedFinalPrice - buyItemsTotal);

        // Use provided originalPrice (from selected variant) or fallback to product variant_price
        const price = originalPrice || parseFloat(product.product_details?.variant_price || '0');
        if (price > 0 && getItemFinalPrice < price) {
          // Calculate percentage off: ((originalPrice - finalPrice) / originalPrice) * 100
          const percentageOff = ((price - getItemFinalPrice) / price) * 100;
          return `${Math.round(percentageOff)}% off`;
        }

        // Fallback to showing discount amount if percentage calculation isn't possible
        const discountAmount = Math.max(0, price - getItemFinalPrice);
        if (discountAmount > 0) {
          return `Save RM ${discountAmount.toFixed(2)}`;
        }
        return '';
      }
    }

    // Fall back to per-item discount
    if (product.discount_type === 'fixed') {
      return `Save RM ${product.discount_value.toFixed(2)}`;
    } else if (product.discount_type === 'percentage') {
      return `${Math.round(product.discount_value)}% off`;
    }
    return '';
  };

  const getProductImage = (product: RedeemableProduct): string => {
    if (product.product_details?.product_data?.images?.[0]?.url) {
      return product.product_details.product_data.images[0].url;
    }
    return 'https://via.placeholder.com/120x120?text=No+Image';
  };

  const getProductTitle = (product: RedeemableProduct): string => {
    if (product.product_details?.product_data?.title) {
      return product.product_details.product_data.title;
    }
    return product.product_details?.product_title || 'Unknown Product';
  };

  const handleViewImage = (imageUrl: string) => {
    if (!imageUrl) return;
    const normalized = imageUrl.startsWith('http') ? imageUrl : `https:${imageUrl}`;
    setFullScreenImage(normalized);
  };

  // Get available options from product variants
  const getAvailableOptions = (product: RedeemableProduct): Map<string, string[]> => {
    const optionsMap = new Map<string, string[]>();
    const variants = product.product_details?.product_data?.variants || [];

    variants.forEach((variant) => {
      variant.selectedOptions?.forEach((option) => {
        if (!optionsMap.has(option.name)) {
          optionsMap.set(option.name, []);
        }
        const values = optionsMap.get(option.name)!;
        if (!values.includes(option.value)) {
          values.push(option.value);
        }
      });
    });

    return optionsMap;
  };

  // Find variant by selected options
  const findVariantByOptions = (
    product: RedeemableProduct,
    selectedOptions: Map<string, string>
  ): string | null => {
    const variants = product.product_details?.product_data?.variants || [];

    for (const variant of variants) {
      const variantOptions = variant.selectedOptions || [];

      // Check if all selected options match this variant
      const allMatch = Array.from(selectedOptions.entries()).every(([name, value]) => {
        return variantOptions.some((opt) => opt.name === name && opt.value === value);
      });

      // Also check that variant has no extra options (exact match)
      if (allMatch && variantOptions.length === selectedOptions.size) {
        // Extract numeric ID from GID format if needed
        const variantId = variant.id;
        if (variantId.startsWith('gid://shopify/ProductVariant/')) {
          return variantId.replace('gid://shopify/ProductVariant/', '');
        }
        return variantId;
      }
    }

    return null;
  };

  // Handle option selection change
  const handleOptionChange = (
    discountId: string,
    discountIndex: number,
    groupIndex: number,
    optionName: string,
    optionValue: string,
    product: RedeemableProduct
  ) => {
    const discountKey = getDiscountStateKey(discountId, discountIndex);
    const optionKey = getVariantOptionKey(discountKey, groupIndex);

    // Update options state
    setVariantOptions((prev) => {
      const newOptions = new Map(prev);
      if (!newOptions.has(optionKey)) {
        newOptions.set(optionKey, new Map());
      }
      const options = new Map(newOptions.get(optionKey)!);

      if (optionValue === '') {
        // Remove option if cleared
        options.delete(optionName);
      } else {
        options.set(optionName, optionValue);
      }

      newOptions.set(optionKey, options);

      // Find the matching variant with updated options
      const matchedVariantId = findVariantByOptions(product, options);
      if (matchedVariantId) {
        // Update selection with the matched variant ID
        handleSelection(discountId, discountIndex, groupIndex, matchedVariantId);
      } else {
        // Clear selection if no variant matches
        handleSelection(discountId, discountIndex, groupIndex, '');
      }

      return newOptions;
    });
  };

  const renderProductCard = (
    product: RedeemableProduct,
    discountId: string,
    discountIndex: number,
    groupIndex: number,
    isSelected: boolean,
    uniqueKey: string,
    potentialDiscount?: PotentialDiscount
  ) => {
    const variants = product.product_details?.product_data?.variants || [];
    const discountKey = getDiscountStateKey(discountId, discountIndex);
    const selectedVariantId = selections.get(discountKey)?.get(groupIndex);


    // Find the actual selected variant to get its price
    let displayVariant = null;
    if (selectedVariantId) {
      displayVariant = variants.find((v: any) => {
        const vId = v.id.startsWith('gid://shopify/ProductVariant/')
          ? v.id.replace('gid://shopify/ProductVariant/', '')
          : v.id;
        return vId === selectedVariantId;
      });
    }

    // Use selected variant price if available, otherwise use first variant price, otherwise default
    const variantPrice = displayVariant?.price ||
                        (variants.length > 0 ? variants[0].price : null) ||
                        product.product_details?.variant_price ||
                        '0';
    const originalPrice = formatPrice(variantPrice);

    // Calculate discounted price based on selected variant
    const discountedPrice = (() => {
      const price = parseFloat(variantPrice);
      if (isNaN(price)) return 'N/A';

      let discountAmount = 0;
      let finalPrice = price;

      // Check discount_applicable_to first - use whole discount if applicable
      if (potentialDiscount?.discount_applicable_to === 'whole' && potentialDiscount.whole_discount_type && potentialDiscount.whole_discount_value !== undefined) {
        // Convert whole_discount_value to number to handle string values from API
        const wholeDiscountValue = typeof potentialDiscount.whole_discount_value === 'number' 
          ? potentialDiscount.whole_discount_value 
          : parseFloat(String(potentialDiscount.whole_discount_value)) || 0;
        
        if (potentialDiscount.whole_discount_type === 'fixed') {
          discountAmount = Math.min(wholeDiscountValue, price);
          finalPrice = Math.max(0, price - discountAmount);
        } else if (potentialDiscount.whole_discount_type === 'percentage') {
          discountAmount = (price * wholeDiscountValue) / 100;
          finalPrice = Math.max(0, price - discountAmount);
        } else if (potentialDiscount.whole_discount_type === 'final_price') {
          // For final_price type: final_price (whole_discount_value) - buy items total = get item final price
          const buyItemsTotal = potentialDiscount.buyItems?.reduce((sum, buyItem) => {
            return sum + (buyItem.item.price || 0);
          }, 0) || 0;
          const combinedFinalPrice = wholeDiscountValue;
          finalPrice = Math.max(0, combinedFinalPrice - buyItemsTotal);
          discountAmount = price - finalPrice;
        }
      } else {
        // Fall back to per-item discount
        if (product.discount_type === 'fixed') {
          discountAmount = product.discount_value;
          finalPrice = Math.max(0, price - discountAmount);
        } else if (product.discount_type === 'percentage') {
          discountAmount = (price * product.discount_value) / 100;
          finalPrice = Math.max(0, price - discountAmount);
        }
      }

      return `RM ${finalPrice.toFixed(2)}`;
    })();

    const discountText = getDiscountText(product, potentialDiscount, parseFloat(variantPrice));
    const productImage = getProductImage(product);
    const productTitle = getProductTitle(product);

    // Check if product has only one in-stock variant (no options needed; others may be OOS)
    const hasOnlyOneVariant =
      variants.filter(
        (v: any) => v.inventoryQuantity === undefined || v.inventoryQuantity > 0
      ).length === 1;

    // Check if selected variant is out of stock
    const isOutOfStock = (() => {
      if (!selectedVariantId) {
        // If no variant selected, check if first variant is out of stock
        const firstVariant = variants.length > 0 ? variants[0] : null;
        if (firstVariant && firstVariant.inventoryQuantity !== undefined) {
          return firstVariant.inventoryQuantity <= 0;
        }
        return false; // If no inventory data, assume in stock
      }
      const variant = displayVariant || variants.find((v: any) => {
        const vId = v.id.startsWith('gid://shopify/ProductVariant/')
          ? v.id.replace('gid://shopify/ProductVariant/', '')
          : v.id;
        return vId === selectedVariantId;
      });
      if (variant && variant.inventoryQuantity !== undefined) {
        return variant.inventoryQuantity <= 0;
      }
      return false; // If no inventory data, assume in stock
    })();

    // Get variant ID for click handler
    const variantIdForClick = selectedVariantId || (variants.length > 0 ? (variants[0].id.startsWith('gid://shopify/ProductVariant/')
      ? variants[0].id.replace('gid://shopify/ProductVariant/', '')
      : variants[0].id) : '');

    return (
      <div
        key={uniqueKey}
        onClick={() => !isOutOfStock && variantIdForClick && handleSelection(discountId, discountIndex, groupIndex, variantIdForClick)}
        style={{
          position: 'relative',
          flexShrink: 0,
          width: '180px',
          height: 'auto',
          backgroundColor: 'transparent',
          border: isOutOfStock ? '1px solid #ddd' : (isSelected ? '2px solid #28a745' : '1px solid #e0e0e0'),
          borderRadius: '6px',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isOutOfStock ? 'none' : (isSelected ? '0 4px 8px rgba(40, 167, 69, 0.2)' : '0 2px 4px rgba(0,0,0,0.1)'),
          transition: 'all 0.2s',
          opacity: isOutOfStock ? 0.6 : 1,
          cursor: isOutOfStock ? 'not-allowed' : 'pointer',
        }}
      >
        {/* Selection Indicator */}
        {isSelected && !isOutOfStock && (
          <div
            style={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              backgroundColor: '#28a745',
              color: '#fff',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              zIndex: 10,
            }}
          >
            ✓
          </div>
        )}

        {/* Out of Stock Indicator */}
        {isOutOfStock && (
          <div
            style={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              backgroundColor: '#dc3545',
              color: '#fff',
              fontSize: '9px',
              fontWeight: '600',
              padding: '3px 6px',
              borderRadius: '3px',
              zIndex: 10,
            }}
          >
            Out of Stock
          </div>
        )}

        {/* Product Image */}
        <div
          style={{
            position: 'relative',
            borderRadius: '6px 6px 0 0',
            overflow: 'hidden',
            width: '100%',
            aspectRatio: '2 / 3',
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleViewImage(productImage);
            }}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '40px',
              height: '40px',
              border: 'none',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              padding: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: '#fff',
              cursor: 'pointer',
              zIndex: 30,
              clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
            }}
            aria-label="View image in full screen"
          >
            ⛶
          </button>
          <img
            src={productImage}
            alt={productTitle}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                'https://via.placeholder.com/180x180?text=No+Image';
            }}
          />
          {/* Overlay Content */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              gap: '10px',
              padding: '16px 12px 12px',
              background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0) 75%)',
              color: '#fff',
            }}
          >
            <div
              style={{
                fontSize: '13px',
                fontWeight: 600,
                lineHeight: 1.2,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {productTitle}
            </div>
            {/* Variant Options Selector - Only show if product has multiple variants */}
            {!hasOnlyOneVariant && (() => {
              const availableOptions = getAvailableOptions(product);
              if (availableOptions.size === 0) return null;

              const optionStateKey = getVariantOptionKey(discountKey, groupIndex);
              const selectedOptions = variantOptions.get(optionStateKey) || new Map<string, string>();

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {Array.from(availableOptions.entries()).map(([optionName, optionValues]) => {
                    const selectedValue = selectedOptions.get(optionName) || '';

                    return (
                      <div
                        key={optionName}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <label
                          style={{
                            fontSize: '10px',
                            fontWeight: 500,
                            color: 'rgba(255, 255, 255, 0.85)',
                          }}
                        >
                          {optionName}:
                        </label>
                        <select
                          value={selectedValue}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleOptionChange(discountId, discountIndex, groupIndex, optionName, e.target.value, product);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: '11px',
                            padding: '5px 6px',
                            border: '1px solid rgba(255, 255, 255, 0.7)',
                            borderRadius: '4px',
                            backgroundColor: 'rgba(255, 255, 255, 0.94)',
                            color: '#222',
                            cursor: 'pointer',
                            width: '100%',
                          }}
                        >
                          <option value="">Select {optionName}</option>
                          {optionValues.map((value) => {
                            const variantOptionKey = getVariantOptionKey(discountKey, groupIndex);
                            const currentOptions = new Map(variantOptions.get(variantOptionKey) || new Map());
                            currentOptions.set(optionName, value);

                            const matchingVariant = variants.find((v: any) => {
                              return v.selectedOptions?.every((opt: any) => {
                                const expectedValue = currentOptions.get(opt.name);
                                return !expectedValue || opt.value === expectedValue;
                              });
                            });

                            const isVariantOutOfStock =
                              matchingVariant &&
                              matchingVariant.inventoryQuantity !== undefined &&
                              matchingVariant.inventoryQuantity <= 0;

                            return (
                              <option key={value} value={value} disabled={isVariantOutOfStock}>
                                {value} {isVariantOutOfStock ? '(Out of Stock)' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {/* Price */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              {discountedPrice !== 'N/A' && originalPrice !== 'N/A' ? (
                <>
                  <div
                    style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#9FF08C',
                      textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {discountedPrice}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.7)',
                      textDecoration: 'line-through',
                    }}
                  >
                    {originalPrice}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#9FF08C',
                    textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                    letterSpacing: '0.01em',
                  }}
                >
                  {originalPrice}
                </div>
              )}
            </div>
          </div>
          {discountText && (
            <div
              style={{
                position: 'absolute',
              top: '4px',
              right: '4px',
              backgroundColor: '#ff4444',
              color: '#fff',
              fontSize: '9px',
              fontWeight: '600',
              padding: '2px 5px',
              borderRadius: '3px',
              }}
            >
              {discountText}
            </div>
          )}
        </div>

      </div>
    );
  };

  const renderDiscountSection = (
    potentialDiscount: PotentialDiscount,
    discountIndex: number,
    stackCount = 1
  ) => {
    const { discount_id, discount_title, redeemableGroups } = potentialDiscount;
    const discountKey = getDiscountStateKey(discount_id, discountIndex);

    if (!redeemableGroups || redeemableGroups.length === 0) {
      return null;
    }

    // Get selections for THIS specific discount (not shared across discounts)
    // Always get fresh from state to avoid closure issues
    const discountSelectionsMap = selections.get(discountKey);
    const discountSelections = discountSelectionsMap ? new Map(discountSelectionsMap) : new Map<number, string>();
    const allGroupsSelected = redeemableGroups.every((_, idx) =>
      discountSelections.has(idx)
    );

    const isAddingDiscount = addingDiscounts.has(discountKey);
    const handleAddSelected = async () => {
      if (!allGroupsSelected) {
        return;
      }

      const payloadItems: RedeemableSelectionItemPayload[] = [];
      const encounteredDiscountKeys = new Set<string>([discountKey]);

      for (let groupIdx = 0; groupIdx < redeemableGroups.length; groupIdx += 1) {
        const group = redeemableGroups[groupIdx];
        const resolvedDiscountIndex =
          typeof group.discountIndex === 'number' ? group.discountIndex : discountIndex;
        const discountKeyForGroup = getDiscountStateKey(discount_id, resolvedDiscountIndex);
        encounteredDiscountKeys.add(discountKeyForGroup);

        const discountSelectionsForGroup = selections.get(discountKeyForGroup);
        const selectedVariantId = discountSelectionsForGroup?.get(groupIdx);
        if (!selectedVariantId) {
          continue;
        }

        const currentSlideIndex = getCurrentSlideIndex(discount_id, resolvedDiscountIndex, groupIdx);
        const currentItem = group.items[currentSlideIndex];
        if (!currentItem) {
          continue;
        }

        const variants = currentItem.product_details?.product_data?.variants || [];
        const selectedVariantIdStr = String(selectedVariantId);
        const variantNumericId = extractNumericId(selectedVariantIdStr);

        if (variantNumericId === null) {
          console.warn('Unable to parse variant ID for selection:', selectedVariantIdStr);
          continue;
        }

        const matchedVariant = variants.find((variant: any) => {
          const variantNumeric = extractNumericId(variant.id);
          return variantNumeric === variantNumericId;
        });

        const productNumericId = extractNumericId(
          currentItem.product_details?.product_id ||
          currentItem.product_details?.product_data?.id
        );

        const priceSource =
          matchedVariant?.price ??
          currentItem.product_details?.variant_price ??
          variants[0]?.price ??
          '0';
        const priceInCents = parsePriceToCents(priceSource);

        payloadItems.push({
          variantId: variantNumericId,
          quantity: currentItem.quantity || 1,
          productId: productNumericId ?? 0,
          price: priceInCents,
          title: getProductTitle(currentItem),
        });
      }

      if (payloadItems.length !== redeemableGroups.length) {
        alert('Please ensure all selections are valid before adding to cart.');
        return;
      }

      setAddingDiscounts((prev) => {
        const next = new Set(prev);
        next.add(discountKey);
        return next;
      });

      try {
        const success = await onRedeemableAdd({
          discountId: discount_id,
          discountIndex,
          items: payloadItems,
        });

        if (success) {
          setSelections((prev) => {
            const next = new Map(prev);
            encounteredDiscountKeys.forEach((key) => {
              next.delete(key);
            });
            return next;
          });

          setVariantOptions((prev) => {
            const next = new Map(prev);
            encounteredDiscountKeys.forEach((key) => {
              Array.from(next.keys()).forEach((optionKey) => {
                if (optionKey.startsWith(`${key}-`)) {
                  next.delete(optionKey);
                }
              });
            });
            return next;
          });
        }
      } catch (error: any) {
        console.error('Failed to add redeemable items:', error);
        alert(error?.message || 'Failed to add products to cart');
      } finally {
        setAddingDiscounts((prev) => {
          const next = new Set(prev);
          next.delete(discountKey);
          return next;
        });
      }
    };

    const groupCount = redeemableGroups.length;
    const baseGroupWidth = 180;
    const horizontalGap = 8;
    const horizontalPadding = 16;
    const calculatedWidth =
      groupCount > 0
        ? groupCount * baseGroupWidth + Math.max(groupCount - 1, 0) * horizontalGap + horizontalPadding
        : 200;
    const cardWidth = Math.max(200, calculatedWidth);
    const stackSpacing = 6;
    const maxVisualLayers = Math.min(Math.max(stackCount - 1, 0), 2);
    const visualOffset = stackSpacing * maxVisualLayers;
    const containerWidth = cardWidth + visualOffset;
    const stackedLayers = Array.from({ length: maxVisualLayers }).map((_, idx) => {
      const layerDepth = maxVisualLayers - idx;
      return (
        <div
          key={`stack-layer-${idx}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            transform: `translate(${layerDepth * stackSpacing}px, ${layerDepth * stackSpacing}px)`,
            borderRadius: '8px',
            backgroundColor: '#ffe9ad',
            border: '1px solid #ffd36a',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
            zIndex: idx + 1,
            pointerEvents: 'none',
          }}
        />
      );
    });
    const outerPadding = visualOffset;

    return (
      <div
        key={`discount-section-${discount_id}`}
        data-discount-id={discount_id}
        style={{
          position: 'relative',
          flexShrink: 0,
          width: `${containerWidth}px`,
          paddingBottom: outerPadding,
          paddingTop: stackCount > 1 ? outerPadding : 0,
        }}
      >
        <div
          style={{
            position: 'relative',
            width: `${cardWidth}px`,
            height: '100%',
            overflow: 'visible',
          }}
        >
          {stackedLayers}
          <div
            style={{
              position: 'relative',
              zIndex: maxVisualLayers + 2,
              width: `${cardWidth}px`,
              height: '100%',
            }}
          >
            <div
              style={{
                position: 'relative',
                flexShrink: 0,
                width: `${cardWidth}px`,
                backgroundColor: '#fff9e6',
                border: '1px solid #ffd700',
                borderRadius: '6px',
                padding: '8px',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {stackCount > 1 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    transform: 'translate(50%, -50%)',
                    backgroundColor: '#6F1920',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  {`${stackCount}x`}
                </div>
              )}
              {/* Discount Header */}
              <div style={{ marginBottom: '8px' }}>
                <h3
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    margin: 0,
                    color: '#4A2A2D',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {discount_title}
                </h3>
              </div>

              {/* Groups - All horizontally */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'stretch',
                  gap: '8px',
                  flexWrap: 'nowrap',
                  marginBottom: '8px',
                }}
              >
                {redeemableGroups.map((group, groupIdx) => {
                  const resolvedDiscountIndex = typeof group.discountIndex === 'number'
                    ? group.discountIndex
                    : discountIndex;
                  const currentSlideIndex = getCurrentSlideIndex(discount_id, resolvedDiscountIndex, groupIdx);
                  const currentItem = group.items[currentSlideIndex];
                  const hasMultipleItems = group.items.length > 1;
                  return (
                    <React.Fragment key={`group-${discount_id}-${resolvedDiscountIndex}-${groupIdx}`}>
                      {/* Group Items - Slideshow */}
                      <div
                        style={{
                          flexShrink: 0,
                          width: '180px',
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {/* Slideshow Container */}
                        <div
                          style={{
                            position: 'relative',
                            width: '180px',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* Navigation Arrows */}
                          {hasMultipleItems && (
                            <>
                              <button
                                onClick={() => handleSlide(discount_id, resolvedDiscountIndex, groupIdx, 'prev', group.items.length)}
                                style={{
                                  position: 'absolute',
                                  left: '4px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  border: '1px solid #ddd',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: '#333',
                                  zIndex: 20,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fff';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                                }}
                              >
                                ‹
                              </button>
                              <button
                                onClick={() => handleSlide(discount_id, resolvedDiscountIndex, groupIdx, 'next', group.items.length)}
                                style={{
                                  position: 'absolute',
                                  right: '4px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                  border: '1px solid #ddd',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  color: '#333',
                                  zIndex: 20,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fff';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                                }}
                              >
                                ›
                              </button>
                            </>
                          )}

                          {/* Current Item Display */}
                          {currentItem && (() => {
                            const variants = currentItem.product_details?.product_data?.variants || [];

                            // Get the currently selected variant ID for this group
                            const discountKeyForGroup = getDiscountStateKey(discount_id, resolvedDiscountIndex);
                            const discountSelectionsForThisDiscount = selections.get(discountKeyForGroup);
                            const selectedVariantForThisGroup = discountSelectionsForThisDiscount?.get(groupIdx);

                            // Check if this product's variants include the selected variant
                            const isItemSelected = selectedVariantForThisGroup !== undefined && (() => {
                              if (!selectedVariantForThisGroup) return false;
                              return variants.some((v: any) => {
                                const vId = v.id.startsWith('gid://shopify/ProductVariant/')
                                  ? v.id.replace('gid://shopify/ProductVariant/', '')
                                  : v.id;
                                return vId === selectedVariantForThisGroup;
                              });
                            })();

                            // Create a unique key based on product and selected variant
                            const baseVariantId = currentItem.product_details?.variant_id || 'no-variant';
                            const uniqueKey = `${discountKeyForGroup}-${groupIdx}-${baseVariantId}-${selectedVariantForThisGroup || 'none'}`;

                            return renderProductCard(
                              currentItem,
                              discount_id,
                              resolvedDiscountIndex,
                              groupIdx,
                              isItemSelected,
                              uniqueKey,
                              potentialDiscount
                            );
                          })()}

                          {/* Slide Indicators */}
                          {hasMultipleItems && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '4px',
                                marginTop: '4px',
                              }}
                            >
                              {group.items.map((_, itemIdx) => (
                                <div
                                  key={itemIdx}
                                  onClick={() => {
                                    setSlideIndices((prev) => {
                                      const newIndices = new Map(prev);
                                      const discountKeyForGroup = getDiscountStateKey(discount_id, resolvedDiscountIndex);
                                      const groupIndices = new Map(newIndices.get(discountKeyForGroup) || new Map());
                                      groupIndices.set(groupIdx, itemIdx);
                                      newIndices.set(discountKeyForGroup, groupIndices);
                                      return newIndices;
                                    });
                                  }}
                                  style={{
                                    width: currentSlideIndex === itemIdx ? '20px' : '6px',
                                    height: '6px',
                                    borderRadius: '3px',
                                    backgroundColor: currentSlideIndex === itemIdx ? '#6F1920' : '#C5A3A6',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Add to Cart Button */}
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                <button
                  onClick={handleAddSelected}
                  disabled={!allGroupsSelected || isAddingDiscount}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: allGroupsSelected && !isAddingDiscount ? '#6F1920' : '#E2D1D2',
                    color: allGroupsSelected && !isAddingDiscount ? '#fff' : '#430F13',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: allGroupsSelected && !isAddingDiscount ? 'pointer' : 'not-allowed',
                    transition: 'background-color 0.2s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    if (allGroupsSelected && !isAddingDiscount) {
                      e.currentTarget.style.backgroundColor = '#59141A';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (allGroupsSelected && !isAddingDiscount) {
                      e.currentTarget.style.backgroundColor = '#6F1920';
                    }
                  }}
                >
                  {isAddingDiscount
                    ? 'Adding...'
                    : allGroupsSelected
                    ? `Add ${redeemableGroups.length} Product${redeemableGroups.length > 1 ? 's' : ''}`
                    : `Select ${redeemableGroups.length}`
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {fullScreenImage && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setFullScreenImage(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              onClick={() => setFullScreenImage(null)}
              aria-label="Close image preview"
              style={{
                position: 'absolute',
                top: '-16px',
                right: '-16px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '18px',
                transition: 'background-color 0.2s, transform 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            <img
              src={fullScreenImage}
              alt="Full screen preview"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>
        </div>
      )}
      <div
        style={{
          marginBottom: '16px',
          overflowX: 'auto',
        }}
      >
      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'nowrap',
          alignItems: 'stretch',
        }}
      >
        {groupedDiscounts.map(({ representative, representativeIndex, count }) =>
          renderDiscountSection(representative, representativeIndex, count)
        )}
      </div>
      </div>
    </>
  );
};
